import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjectDto, ProjectRuleDto, Severity } from "@reviewer73/shared";
import { RotateCcw } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../api/client.js";
import { GroupsEditor } from "../components/groups-editor.js";
import { InheritedNumberField } from "../components/inherited-number-field.js";
import { ModelPicker } from "../components/model-picker.js";
import { EmptyNote } from "../components/empty-note.js";
import { Rack } from "../components/rack.js";
import { ReviewRow, ReviewRowSkeleton } from "../components/review-row.js";
import { SeverityBadge } from "../components/severity-badge.js";
import { StaggerBody, StaggerContainer, StaggerItem, StaggerRow } from "../components/stagger.js";
import { Strip, StripField, StripFields } from "../components/strip.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.js";
import { Switch } from "../components/ui/switch.js";
import { Table, TableCell, TableEmpty, TableHead, TableHeader, TableRow, TableSkeleton } from "../components/ui/table.js";
import { formatDate, repoName } from "../lib/format.js";

const SEVERITIES: Severity[] = ["low", "medium", "high", "critical"];
const SEVERITY_LABEL: Record<Severity, string> = {
  low: "niedrig",
  medium: "mittel",
  high: "hoch",
  critical: "kritisch",
};

export function ProjectDetail() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [mrIid, setMrIid] = useState("");

  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const allRules = useQuery({ queryKey: ["rules"], queryFn: api.rules });
  const rules = useQuery({ queryKey: ["project-rules", id], queryFn: () => api.projectRules(id) });
  const reviews = useQuery({ queryKey: ["project-reviews", id], queryFn: () => api.projectReviews(id) });
  // Needed to show what an empty limit field inherits.
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const knownGroups = [...new Set((allRules.data?.rules ?? []).map((r) => r.group))];

  const invalidateRules = () => queryClient.invalidateQueries({ queryKey: ["project-rules", id] });

  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.updateProject>[1]) => api.updateProject(id, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: ["projects"] });
      const previous = queryClient.getQueryData<ProjectDto[]>(["projects"]);
      queryClient.setQueryData<ProjectDto[]>(["projects"], (old) =>
        old?.map((p) => (p.id === id ? { ...p, ...body } : p)),
      );
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      queryClient.setQueryData(["projects"], ctx?.previous);
      toast.error("Änderung fehlgeschlagen", { description: (err as Error).message });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      invalidateRules();
    },
  });

  const setOverride = useMutation({
    mutationFn: (vars: { ruleId: string; enabled: boolean | null; severity: Severity | null }) =>
      api.setOverride(id, vars.ruleId, { enabled: vars.enabled, severity: vars.severity }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["project-rules", id] });
      const previous = queryClient.getQueryData<ProjectRuleDto[]>(["project-rules", id]);
      queryClient.setQueryData<ProjectRuleDto[]>(["project-rules", id], (old) =>
        old?.map((r) =>
          r.id === vars.ruleId
            ? {
                ...r,
                effectiveEnabled: vars.enabled ?? r.effectiveEnabled,
                effectiveSeverity: vars.severity ?? r.effectiveSeverity,
                overridden: true,
              }
            : r,
        ),
      );
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      queryClient.setQueryData(["project-rules", id], ctx?.previous);
      toast.error("Änderung fehlgeschlagen", { description: (err as Error).message });
    },
    onSettled: invalidateRules,
  });
  const clearOverride = useMutation({
    mutationFn: (ruleId: string) => api.clearOverride(id, ruleId),
    onSuccess: invalidateRules,
  });
  const trigger = useMutation({
    mutationFn: ({ iid, scope }: { iid: number; scope: "full" | "architecture" }) =>
      api.createReview({ projectId: id, mrIid: iid, scope }),
    onSuccess: (_data, variables) => {
      setMrIid("");
      toast.success(variables.scope === "architecture" ? "Architektur-Review gestartet" : "Review gestartet");
      void queryClient.invalidateQueries({ queryKey: ["project-reviews", id] });
    },
    onError: (err) => toast.error("Review konnte nicht gestartet werden", { description: (err as Error).message }),
  });

  const project = projects.data?.find((p) => p.id === id);
  if (projects.isLoading) return <p className="p-4 text-sm text-muted-foreground">Projekt wird geladen…</p>;
  if (!project) {
    return (
      <div className="tile p-6 text-sm text-muted-foreground">Projekt nicht gefunden.</div>
    );
  }

  // Same three layers as the backend resolves: project, then global, then env.
  // Null while the settings query is still in flight — the fields then show "…"
  // rather than a guessed default.
  const globalValue = (field: "defaultMaxDiffLines" | "fanOutAboveLines" | "maxSubagents") =>
    settings.data ? (settings.data.stored[field] ?? settings.data.fallback[field]) : null;
  const effectiveMaxDiff = project.maxDiffLines ?? globalValue("defaultMaxDiffLines");
  const effectiveFanOut = project.fanOutAboveLines ?? globalValue("fanOutAboveLines");

  const gitlabProjectId = project.gitlabProjectId;
  const [gitlabIdDraft, setGitlabIdDraft] = useState(String(gitlabProjectId));
  function commitGitlabId() {
    const trimmed = gitlabIdDraft.trim();
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      setGitlabIdDraft(String(gitlabProjectId));
      return;
    }
    if (parsed !== gitlabProjectId) update.mutate({ gitlabProjectId: parsed });
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/projects" className="text-xs text-muted-foreground hover:text-foreground">
          ← Projekte
        </Link>
        <h1 className="mt-1 text-xl font-bold tracking-tight">{repoName(project.pathWithNamespace)}</h1>
      </div>

      {/* The project's own head strip: live plastic if reviews are on. */}
      <Strip holderColor={project.enabled ? "var(--hold-running)" : "var(--hold-skipped)"}>
        <StripFields className="flex-wrap sm:flex-nowrap">
          <StripField label="Reviews" className="min-w-28">
            {project.enabled ? "aktiv" : "inaktiv"}
          </StripField>
          <StripField label="Modell" className="min-w-52">
            <ModelPicker value={project.model} onChange={(model) => update.mutate({ model })} />
          </StripField>
          <StripField label="Gruppen" className="min-w-32 flex-1" valueClassName="overflow-visible whitespace-normal">
            <GroupsEditor
              groups={project.groups}
              knownGroups={knownGroups}
              onChange={(groups) => update.mutate({ groups })}
            />
          </StripField>
          <StripField label="GitLab-Projekt-ID" className="min-w-24">
            <Input
              value={gitlabIdDraft}
              inputMode="numeric"
              aria-label="GitLab-Projekt-ID"
              onChange={(e) => setGitlabIdDraft(e.target.value.replace(/[^\d]/g, ""))}
              onBlur={commitGitlabId}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") setGitlabIdDraft(String(gitlabProjectId));
              }}
              className="w-24 tabular-nums"
            />
          </StripField>
        </StripFields>
      </Strip>

      <Rack
        label="Limits & Fan-out"
        hint="Leeres Feld heisst: der globale Wert aus den Einstellungen gilt."
      >
        <div className="tile space-y-4 p-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <dt className="text-xs font-semibold">Diff-Limit</dt>
              <dd>
                <InheritedNumberField
                  aria-label="Diff-Limit"
                  value={project.maxDiffLines}
                  inherited={globalValue("defaultMaxDiffLines")}
                  min={1}
                  onChange={(maxDiffLines) => update.mutate({ maxDiffLines })}
                />
              </dd>
              <p className="text-[11px] text-muted-foreground">
                Darüber wird der MR übersprungen.
              </p>
            </div>
            <div className="space-y-1.5">
              <dt className="text-xs font-semibold">Fan-out ab</dt>
              <dd>
                <InheritedNumberField
                  aria-label="Fan-out ab"
                  value={project.fanOutAboveLines}
                  inherited={globalValue("fanOutAboveLines")}
                  onChange={(fanOutAboveLines) => update.mutate({ fanOutAboveLines })}
                />
              </dd>
              <p className="text-[11px] text-muted-foreground">
                Darüber teilt der Agent den Diff auf Subagents auf.
              </p>
            </div>
            <div className="space-y-1.5">
              <dt className="text-xs font-semibold">Max. Subagents</dt>
              <dd>
                <InheritedNumberField
                  aria-label="Maximale Subagents"
                  value={project.maxSubagents}
                  inherited={globalValue("maxSubagents")}
                  min={1}
                  onChange={(maxSubagents) => update.mutate({ maxSubagents })}
                />
              </dd>
            </div>
            <div className="space-y-1.5">
              <dt className="text-xs font-semibold">Subagent-Modell</dt>
              <dd className="flex items-center gap-1">
                <ModelPicker
                  className="max-w-64 flex-1"
                  value={project.subagentModel ?? ""}
                  onChange={(subagentModel) => update.mutate({ subagentModel })}
                />
                {project.subagentModel === null ? null : (
                  <button
                    type="button"
                    onClick={() => update.mutate({ subagentModel: null })}
                    title="Auf das Projekt-Modell zurücksetzen"
                    aria-label="Subagent-Modell auf das Projekt-Modell zurücksetzen"
                    className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
              </dd>
              <p className="text-[11px] text-muted-foreground">
                Leer: die Subagents laufen auf dem Projekt-Modell.
              </p>
            </div>
            <div className="space-y-1.5">
              <dt className="text-xs font-semibold">Architektur-Modell</dt>
              <dd className="flex items-center gap-1">
                <ModelPicker
                  className="max-w-64 flex-1"
                  value={project.architectureModel ?? ""}
                  onChange={(architectureModel) => update.mutate({ architectureModel })}
                />
                {project.architectureModel === null ? null : (
                  <button
                    type="button"
                    onClick={() => update.mutate({ architectureModel: null })}
                    title="Auf das Projekt-Modell zurücksetzen"
                    aria-label="Architektur-Modell auf das Projekt-Modell zurücksetzen"
                    className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
              </dd>
              <p className="text-[11px] text-muted-foreground">
                Beurteilt den MR als Ganzes, beim ersten Review. Leer: Projekt-Modell.
              </p>
            </div>
          </dl>
          {effectiveFanOut === null || effectiveMaxDiff === null ? null : effectiveFanOut >=
            effectiveMaxDiff ? (
            <p className="text-[11px] text-muted-foreground">
              Fan-out ist für dieses Projekt aus: die Schwelle ({effectiveFanOut}) liegt nicht unter
              dem Diff-Limit ({effectiveMaxDiff}).
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Bis {effectiveFanOut} Zeilen ein Agent, bis {effectiveMaxDiff} Zeilen Fan-out, darüber
              übersprungen.
            </p>
          )}
        </div>
      </Rack>

      <Rack label="Review manuell starten">
        <div className="tile space-y-2 p-4">
          <p className="max-w-prose text-xs text-muted-foreground">
            Umgeht das Diff-Limit. Nützlich für übersprungene MRs oder um ein Review zu wiederholen.
            <br />
            <strong>Nur Architektur</strong> beurteilt den MR als Ganzes und sonst nichts: keine
            Zeilen-Findings, kein Secret-Scan, keine bestehende Diskussion wird angefasst. Läuft
            auch dann, wenn der MR seinen automatischen Architektur-Durchgang schon hatte.
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={mrIid}
              onChange={(e) => setMrIid(e.target.value.replace(/\D/g, ""))}
              placeholder="MR-iid"
              className="w-28"
            />
            <Button
              disabled={!mrIid || trigger.isPending}
              onClick={() => trigger.mutate({ iid: Number(mrIid), scope: "full" })}
            >
              {trigger.isPending ? "startet…" : "Review starten"}
            </Button>
            <Button
              variant="outline"
              disabled={!mrIid || trigger.isPending}
              onClick={() => trigger.mutate({ iid: Number(mrIid), scope: "architecture" })}
            >
              Nur Architektur
            </Button>
          </div>
        </div>
      </Rack>

      <Rack label="Regeln für dieses Projekt" count={rules.data?.length}>
        <div className="tile p-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Aktiv</TableHead>
                <TableHead>Regel</TableHead>
                <TableHead>Gruppe</TableHead>
                <TableHead>Engine</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            {rules.isLoading ? (
              <TableSkeleton colSpan={6} />
            ) : !rules.data || rules.data.length === 0 ? (
              <TableEmpty colSpan={6}>
                Keine Regeln in den Gruppen dieses Projekts. Prüfe das Rules-Repo und die
                Gruppen-Zuordnung.
              </TableEmpty>
            ) : (
              <StaggerBody>
                {rules.data.map((rule) => (
                  <StaggerRow key={rule.id}>
                    <TableCell>
                      <Switch
                        checked={rule.effectiveEnabled}
                        onCheckedChange={(enabled) =>
                          setOverride.mutate({
                            ruleId: rule.id,
                            enabled,
                            severity: rule.overridden ? rule.effectiveSeverity : null,
                          })
                        }
                        aria-label={`Regel ${rule.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-mono text-xs">{rule.id}</div>
                      <div className="text-xs text-muted-foreground">{rule.title}</div>
                    </TableCell>
                    <TableCell className="text-xs">{rule.group}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {rule.engine === "gitleaks" ? (
                        <span title="Deterministischer Scanner, nicht im LLM-Prompt">gitleaks</span>
                      ) : (
                        "llm"
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <SeverityBadge severity={rule.effectiveSeverity} />
                        <Select
                          value={rule.effectiveSeverity}
                          onValueChange={(severity) =>
                            setOverride.mutate({
                              ruleId: rule.id,
                              enabled: rule.effectiveEnabled,
                              severity: severity as Severity,
                            })
                          }
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SEVERITIES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {SEVERITY_LABEL[s]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {rule.effectiveSeverity !== rule.defaultSeverity ? (
                        <span className="mt-1 block text-xs text-muted-foreground">
                          Standard: {SEVERITY_LABEL[rule.defaultSeverity]}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right">
                      {rule.overridden ? (
                        <Button variant="link" size="sm" onClick={() => clearOverride.mutate(rule.id)}>
                          zurücksetzen
                        </Button>
                      ) : null}
                    </TableCell>
                  </StaggerRow>
                ))}
              </StaggerBody>
            )}
          </Table>
        </div>
      </Rack>

      <Rack label="Letzte Reviews" count={reviews.data?.reviews.length} trayClassName="space-y-2">
        {reviews.isLoading ? (
          <ReviewRowSkeleton count={3} />
        ) : !reviews.data || reviews.data.reviews.length === 0 ? (
          <EmptyNote>Noch keine Reviews.</EmptyNote>
        ) : (
          <StaggerContainer className="space-y-2">
            {reviews.data.reviews.map((review) => (
              <StaggerItem key={review.id}>
                <ReviewRow review={review} time={formatDate(review.createdAt)} showProject={false} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}
      </Rack>

      <p className="max-w-prose text-xs text-muted-foreground">
        Regelinhalte werden im Rules-Repo per Merge Request geändert. Hier lassen sich Regeln nur
        ein- und ausschalten oder ihre Severity überschreiben.
      </p>
    </div>
  );
}
